import { Point } from "pixi.js";
import { getElementByKey } from "../../../engines/renderingEngine/renderer";
import {
  AUTO_PAN_SPEED,
  EDGE_PAN_THRESHOLD,
  ZOOM_ANIMATION_DURATION,
  TOP_EDGE_OFFSET
} from "./viewport.constants";
import { triggerSyntheticPointerEvent } from "../../../engines/interactionEngine/interactionObserver";
import {
  ClickType,
  InteractionObserverEvent
} from "../../../engines/interactionEngine/interactionObserverConstants";
import { getViewport } from "./viewport.layer";

/**
 * Handles panning of the viewport when a node is dragged near the edges of the screen.
 * */
export const handleViewportPanningOnDrag = ({
  pointerX,
  pointerY
}: {
  pointerX: number;
  pointerY: number;
}): void => {
  const distanceFromLeftEdge = pointerX;
  const distanceFromTopEdge = pointerY;

  // Get the dimensions of the viewport
  const screenWidth = window.innerWidth;
  const screenHeight = window.innerHeight;

  // Get the dimensions of the node being dragged
  // const nodeAdjustedWidth = node.width / 2;
  // const nodeAdjustedHeight = node.height / 2;

  // Calculate if the node is near the edge of the screen
  const distanceFromRightEdge = screenWidth - distanceFromLeftEdge;
  const distanceFromBottomEdge = screenHeight - distanceFromTopEdge;

  const leftEdgeWeight =
    distanceFromLeftEdge < EDGE_PAN_THRESHOLD
      ? (EDGE_PAN_THRESHOLD - distanceFromLeftEdge) / EDGE_PAN_THRESHOLD
      : 0;
  const rightEdgeWeight =
    distanceFromRightEdge < EDGE_PAN_THRESHOLD
      ? (EDGE_PAN_THRESHOLD - distanceFromRightEdge) / EDGE_PAN_THRESHOLD
      : 0;

  // TODO: Temporary fix to offset the top edge threshold by the height
  // of the top bar in the viewport. This is to ensure that we're offsetting
  // the threshold when checking the top edge.
  let topEdgeWeight =
    distanceFromTopEdge < EDGE_PAN_THRESHOLD + TOP_EDGE_OFFSET
      ? (EDGE_PAN_THRESHOLD + TOP_EDGE_OFFSET - distanceFromTopEdge) /
        EDGE_PAN_THRESHOLD
      : 0;
  if (topEdgeWeight > 1) {
    // This ensures that offset doesn't cause the weight to over-speed the panning
    // so we clip it at 1.
    // This essentially means that AT Offset, it has he top speed already, and beyond it
    // it will not increase the speed.(i.e. when cursor is behind the top-bar)
    topEdgeWeight = 1;
  }

  const bottomEdgeWeight =
    distanceFromBottomEdge < EDGE_PAN_THRESHOLD
      ? (EDGE_PAN_THRESHOLD - distanceFromBottomEdge) / EDGE_PAN_THRESHOLD
      : 0;

  // If the node is not near any edge, early return
  let deltaX = 0;
  let deltaY = 0;

  if (
    leftEdgeWeight === 0 &&
    rightEdgeWeight === 0 &&
    topEdgeWeight === 0 &&
    bottomEdgeWeight === 0
  ) {
    return;
  }

  if (leftEdgeWeight > 0) {
    deltaX = leftEdgeWeight * AUTO_PAN_SPEED;
  } else if (rightEdgeWeight > 0) {
    deltaX = -rightEdgeWeight * AUTO_PAN_SPEED;
  }

  if (topEdgeWeight > 0) {
    deltaY = topEdgeWeight * AUTO_PAN_SPEED;
  } else if (bottomEdgeWeight > 0) {
    deltaY = -bottomEdgeWeight * AUTO_PAN_SPEED;
  }

  if (deltaX !== 0 || deltaY !== 0) {
    const viewport = getViewport();
    viewport.x += deltaX;
    viewport.y += deltaY;
  }

  /**
   * Re-trigger the pointer move event to update the cursor position, so that
   * the dragged node is rendered at the correct position.
   * Without this the node would be rendered at the old position until mouse is moved.
   */
  triggerSyntheticPointerEvent({
    globalX: distanceFromLeftEdge,
    globalY: distanceFromTopEdge,
    clickType: ClickType.LEFT_CLICK,
    type: InteractionObserverEvent.POINTERMOVE
  });
};

// This handler will adjust the viewport and focus the node when a any node is selected from the search bar
// Passing a positive xOffset make the centre of the screen to the right of the component and a positive yOffset makes the centre of the screen below component
export const adjustViewportOnNodeSelection = (
  id: string,
  config: {
    xOffset?: number;
    yOffset?: number;
  } = {}
): void => {
  const selectedNode = getElementByKey(id);

  if (!selectedNode) return;

  const { x, y, width } = selectedNode.getLayoutValues();

  const xOffset = config.xOffset ?? 0;
  const yOffset = config.yOffset ?? 0;

  // Calculate the center of the selected node
  const centerX = x + width / 2 - xOffset;
  /** we are not using height for the center as we want to make sure that the node is
      always visible in the viewport at any zoom level as bigger height can
      make the node go out of the viewport 
    */
  const centerY = y + 200 - yOffset;

  // the point should always be at the center of the viewport
  const point = new Point(centerX, centerY);

  getViewport().animate({
    position: point,
    time: ZOOM_ANIMATION_DURATION,
    scaleX: 1,
    scaleY: 1
  });
};
